import { Goal, Experiment } from '@/types/experiment';

class ExperimentService {
  private readonly GOALS_KEY = 'experiment_goals';
  private readonly EXPERIMENTS_KEY = 'experiment_experiments';

  // Goal methods
  getGoals(): Goal[] {
    const stored = localStorage.getItem(this.GOALS_KEY);
    return stored ? JSON.parse(stored).map((goal: any) => ({
      ...goal,
      createdAt: new Date(goal.createdAt)
    })) : [];
  }

  createGoal(name: string, description: string, success_metric: string): Goal {
    const goal: Goal = {
      id: this.generateUUID(),
      name,
      description,
      success_metric,
      createdAt: new Date(),
    };
    
    const goals = this.getGoals();
    goals.push(goal);
    localStorage.setItem(this.GOALS_KEY, JSON.stringify(goals));
    
    return goal;
  }

  updateGoal(id: string, updates: Partial<Goal>): Goal | null {
    const goals = this.getGoals();
    const index = goals.findIndex(goal => goal.id === id);
    
    if (index === -1) return null;
    
    goals[index] = { ...goals[index], ...updates };
    localStorage.setItem(this.GOALS_KEY, JSON.stringify(goals));
    
    return goals[index];
  }

  // Experiment methods
  getExperiments(): Experiment[] {
    const stored = localStorage.getItem(this.EXPERIMENTS_KEY);
    return stored ? JSON.parse(stored).map((exp: any) => ({
      ...exp,
      createdAt: new Date(exp.createdAt),
      completedAt: exp.completedAt ? new Date(exp.completedAt) : undefined
    })) : [];
  }

  createExperiment(name: string, goalId: string, hypothesis: string, parameters: Record<string, any>): Experiment {
    const experiment: Experiment = {
      id: this.generateUUID(),
      name,
      goalId,
      hypothesis,
      parameters,
      status: 'running',
      createdAt: new Date(),
    };
    
    const experiments = this.getExperiments();
    experiments.push(experiment);
    localStorage.setItem(this.EXPERIMENTS_KEY, JSON.stringify(experiments));
    
    return experiment;
  }

  updateExperiment(id: string, updates: Partial<Experiment>): Experiment | null {
    const experiments = this.getExperiments();
    const index = experiments.findIndex(exp => exp.id === id);
    
    if (index === -1) return null;
    
    experiments[index] = { ...experiments[index], ...updates };
    localStorage.setItem(this.EXPERIMENTS_KEY, JSON.stringify(experiments));
    
    return experiments[index];
  }

  // Simulate running an experiment via API
  async runExperimentAPI(experimentId: string, parameters: Record<string, any>): Promise<Record<string, any>> {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Generate mock results
    const results = {
      accuracy: Math.random() * 0.3 + 0.7, // 70-100%
      loss: Math.random() * 0.5 + 0.1, // 0.1-0.6
      training_time: Math.random() * 120 + 30, // 30-150 minutes
      success_metric: Math.random() * 100 + 50,
      timestamp: new Date().toISOString(),
      parameters_used: parameters
    };

    return results;
  }

  updateExperimentStatus(experimentId: string, status: 'running' | 'completed' | 'failed'): void {
    const experiments = this.getExperiments();
    const experimentIndex = experiments.findIndex(exp => exp.id === experimentId);
    
    if (experimentIndex !== -1) {
      experiments[experimentIndex].status = status;
      localStorage.setItem(this.EXPERIMENTS_KEY, JSON.stringify(experiments));
    }
  }

  // Mock API call simulation
  async callExperimentAPI(experimentId: string, metadata: Record<string, any>): Promise<Record<string, any>> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
    // Simulate API response with mock results
    return {
      experimentId,
      timestamp: new Date().toISOString(),
      metrics: {
        accuracy: Math.random() * 0.3 + 0.7, // 70-100%
        precision: Math.random() * 0.2 + 0.8, // 80-100%
        recall: Math.random() * 0.25 + 0.75, // 75-100%
        f1Score: Math.random() * 0.2 + 0.8, // 80-100%
        processingTime: Math.random() * 1000 + 500, // 500-1500ms
      },
      metadata,
      success: Math.random() > 0.3, // 70% success rate
    };
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

export const experimentService = new ExperimentService();
